import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Mail, Phone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { AuthLayout, Field, SocialButtons } from "./Login";

export default function Register() {
  const { register, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // form | otp
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const [pending, setPending] = useState({ email: false, phone: false });
  const [verified, setVerified] = useState({ email: false, phone: false });
  const [codes, setCodes] = useState({ email: "", phone: "" });
  const [devCodes, setDevCodes] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email && !form.phone) return toast.error("Renseignez un e-mail ou un numéro de téléphone");
    setBusy(true);
    try {
      const data = await register(form);
      const v = data.verify || {};
      setPending({ email: !!v.email, phone: !!v.phone });
      const dc = {};
      (data.otps || []).forEach((o) => { if (o.dev_code) dc[o.channel] = o.dev_code; });
      setDevCodes(dc);
      if (v.email || v.phone) { setStep("otp"); toast.success("Compte créé — vérifiez votre code"); }
      else { toast.success("Compte créé — bienvenue !"); navigate("/", { replace: true }); }
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const verify = async (channel) => {
    setBusy(true);
    try {
      await verifyOtp(channel, codes[channel]);
      setVerified((s) => ({ ...s, [channel]: true }));
      toast.success(channel === "email" ? "E-mail vérifié ✅" : "Téléphone vérifié ✅");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const resend = async (channel) => {
    try {
      const r = await resendOtp(channel);
      if (r.dev_code) setDevCodes((s) => ({ ...s, [channel]: r.dev_code }));
      toast.success("Nouveau code envoyé");
    } catch (err) { toast.error(apiError(err)); }
  };

  const channels = ["email", "phone"].filter((c) => pending[c]);
  const allVerified = channels.every((c) => verified[c]);

  if (step === "otp") {
    return (
      <AuthLayout title="Vérification" subtitle="Saisissez le(s) code(s) reçu(s) pour sécuriser votre compte">
        <div className="space-y-4">
          {channels.map((channel) => (
            <div key={channel} className="border border-slate-200 rounded-xl p-4" data-testid={`otp-block-${channel}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-1.5 text-sm font-bold text-slate-700">
                  {channel === "email" ? <Mail className="w-4 h-4 text-[#C85A32]" /> : <Phone className="w-4 h-4 text-[#C85A32]" />}
                  {channel === "email" ? form.email : form.phone}
                </span>
                {verified[channel] && <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700" data-testid={`otp-verified-${channel}`}><ShieldCheck className="w-4 h-4" /> Vérifié</span>}
              </div>
              {!verified[channel] && (
                <>
                  {devCodes[channel] && <p className="text-[11px] text-amber-600 mb-2">Code (démo — envoi non délivré en préversion) : <strong className="font-num">{devCodes[channel]}</strong></p>}
                  <div className="flex gap-2">
                    <input value={codes[channel]} onChange={(e) => setCodes((s) => ({ ...s, [channel]: e.target.value.replace(/\D/g, "") }))} placeholder="Code à 6 chiffres" maxLength={6}
                      className="flex-1 border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E08B00]/50 font-num tracking-[0.3em]" data-testid={`otp-input-${channel}`} />
                    <button onClick={() => verify(channel)} disabled={busy || codes[channel].length < 4} className="bg-[#0F172A] text-white font-bold px-4 rounded-lg disabled:opacity-50" data-testid={`otp-verify-${channel}`}>Vérifier</button>
                  </div>
                  <button onClick={() => resend(channel)} className="mt-2 text-xs text-[#C85A32] font-semibold" data-testid={`otp-resend-${channel}`}>Renvoyer le code</button>
                </>
              )}
            </div>
          ))}
          <button onClick={() => { toast.success("Bienvenue chez TOGOBATI !"); navigate("/", { replace: true }); }} disabled={busy}
            className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg hover:bg-[#A94520] transition active:scale-95 disabled:opacity-50" data-testid="otp-finish">
            {allVerified ? "Terminer" : "Continuer"}
          </button>
          {!allVerified && <p className="text-[11px] text-center text-slate-400">Vous pourrez vérifier plus tard depuis votre profil.</p>}
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Créer un compte" subtitle="Rejoignez TOGOBATI — e-mail ou téléphone, au choix">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nom complet" value={form.name} onChange={set("name")} testid="reg-name" />
        <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="reg-email" required={false} placeholder="vous@exemple.com" />
        <Field label="Téléphone" value={form.phone} onChange={set("phone")} testid="reg-phone" required={false} placeholder="+228 ..." />
        <p className="text-[11px] text-slate-400 -mt-1">Renseignez au moins un e-mail ou un numéro de téléphone. Un code de vérification vous sera envoyé.</p>
        <Field label="Mot de passe" type="password" value={form.password} onChange={set("password")} testid="reg-password" />
        <button disabled={busy} className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg hover:bg-[#A94520] transition active:scale-95 disabled:opacity-50" data-testid="reg-submit">
          {busy ? "Création..." : "Continuer"}
        </button>
      </form>
      <SocialButtons context="register" />
      <p className="text-sm text-center text-slate-500 mt-5">
        Déjà inscrit ? <Link to="/login" className="text-[#C85A32] font-semibold" data-testid="to-login">Se connecter</Link>
      </p>
    </AuthLayout>
  );
}
